import { Capacitor } from '@capacitor/core';
import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signInWithEmailAndPassword as webSignIn,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as webSignOut,
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  doc as webDoc,
  collection as webCollection,
  getDoc as webGetDoc,
  getDocs as webGetDocs,
  setDoc as webSetDoc,
  updateDoc as webUpdateDoc,
  addDoc as webAddDoc,
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  FirebaseFirestore,
  DocumentSnapshot as PluginDocumentSnapshot,
  GetCollectionResult,
} from '@capacitor-firebase/firestore';
import { FirebaseFunctions } from '@capacitor-firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = initializeAuth(app, {
  persistence: Capacitor.isNativePlatform()
    ? indexedDBLocalPersistence
    : browserLocalPersistence,
});

// Initialize auth persistence - resolves when IndexedDB/native storage is ready
let authInitialized: Promise<void>;

if (Capacitor.isNativePlatform()) {
  // On native, wait for FirebaseAuthentication to restore session from secure storage
  authInitialized = FirebaseAuthentication.getCurrentUser()
    .then(() => {})
    .catch(() => {
      // Suppress rejection - proceed even if initial session restore fails
    });
} else {
  // On web, wait for onAuthStateChanged to fire (signals persistence is ready)
  authInitialized = new Promise<void>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });
}

// Use the default Firestore database so the app reads data imported
// into the (default) database.
const dbWeb = getFirestore(app);
const functionsWeb = getFunctions(app);
const storage = getStorage(app);

// Helper types to normalize Firestore results across platforms
export interface DocSnapshot<T = unknown> {
  id: string;
  path: string;
  metadata: unknown;
  data(): T | undefined;
  exists(): boolean;
}

export interface CollectionSnapshot<T = unknown> {
  docs: DocSnapshot<T>[];
}

function wrapDoc<T>(snap: PluginDocumentSnapshot<T>): DocSnapshot<T> {
  return {
    id: snap.id,
    path: snap.path,
    metadata: snap.metadata,
    data: () => (snap.data === null ? undefined : (snap.data as T)),
    exists: () => snap.data !== null,
  };
}

function wrapCollection<T>(res: GetCollectionResult<T>): CollectionSnapshot<T> {
  return { docs: res.snapshots.map((s) => wrapDoc(s)) };
}

// Emulator support
const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST;
if (import.meta.env.DEV && firestoreHost) {
  const [host, port] = firestoreHost.split(':');
  connectFirestoreEmulator(dbWeb, host, Number(port));
  if (Capacitor.isNativePlatform()) {
    FirebaseFirestore.useEmulator({ host, port: Number(port) });
  }
}

const functionsHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST;
if (import.meta.env.DEV && functionsHost) {
  const [host, port] = functionsHost.split(':');
  connectFunctionsEmulator(functionsWeb, host, Number(port));
  if (Capacitor.isNativePlatform()) {
    FirebaseFunctions.useEmulator({ host, port: Number(port) });
  }
}

// ---------- Auth helpers ----------
export async function signInWithEmail(email: string, password: string) {
  if (Capacitor.isNativePlatform()) {
    const { user } = await FirebaseAuthentication.signInWithEmailAndPassword({
      email,
      password,
    });
    return user ?? null;
  }
  const cred = await webSignIn(auth, email, password);
  return cred.user;
}

export async function signInWithGoogle() {
  if (Capacitor.isNativePlatform()) {
    const { user } = await FirebaseAuthentication.signInWithGoogle();
    return user ?? null;
  }
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(auth, provider);
  return cred.user;
}

export async function signOutUser() {
  if (Capacitor.isNativePlatform()) {
    await FirebaseAuthentication.signOut();
  } else {
    await webSignOut(auth);
  }
}

export async function getCurrentUser() {
  if (Capacitor.isNativePlatform()) {
    const { user } = await FirebaseAuthentication.getCurrentUser();
    return user ?? null;
  }
  return auth.currentUser;
}

class ChangePasswordError extends Error {
  code: string;

  constructor(code: string) {
    super(code);
    this.code = code;
  }
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const { user } = await FirebaseAuthentication.getCurrentUser();

  if (!user?.email) {
    throw new ChangePasswordError('auth/no-current-user');
  }

  const usesPasswordProvider = user.providerData.some(
    (provider) => provider.providerId === 'password',
  );
  if (!usesPasswordProvider) {
    throw new ChangePasswordError('auth/unsupported-provider');
  }

  if (currentPassword === newPassword) {
    throw new ChangePasswordError('auth/same-password');
  }

  await FirebaseAuthentication.signInWithEmailAndPassword({
    email: user.email,
    password: currentPassword,
  });
  await FirebaseAuthentication.updatePassword({ newPassword });
}

export function getChangePasswordErrorMessage(error: unknown): string {
  const fallback = 'We couldn\u2019t change your password. Please try again.';
  if (!error || typeof error !== 'object') return fallback;

  const { code, message } = error as { code?: unknown; message?: unknown };
  const details = `${typeof code === 'string' ? code : ''} ${
    typeof message === 'string' ? message : ''
  }`.toLowerCase();

  if (details.includes('no-current-user') || details.includes('no user')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (details.includes('unsupported-provider')) {
    return 'Password changes are only available for accounts that use email and password.';
  }
  if (details.includes('same-password')) {
    return 'Your new password must be different from your current password.';
  }
  if (
    details.includes('wrong-password') ||
    details.includes('invalid-credential') ||
    details.includes('invalid credential')
  ) {
    return 'The current password you entered is incorrect.';
  }
  if (details.includes('weak-password') || details.includes('weak password')) {
    return 'Your new password is too weak. Please choose a stronger password.';
  }
  if (details.includes('too-many-requests') || details.includes('too many')) {
    return 'Too many unsuccessful attempts. Please wait a moment and try again.';
  }
  if (details.includes('network-request-failed') || details.includes('network')) {
    return 'Unable to connect. Check your internet connection and try again.';
  }
  if (details.includes('requires-recent-login') || details.includes('recent login')) {
    return 'For your security, please sign in again before changing your password.';
  }

  return fallback;
}

// ---------- Firestore helpers ----------
function refPath(path: string) {
  return path.split('/');
}

export async function getDocument(path: string) {
  console.log('getDocument called for', path);
  if (Capacitor.isNativePlatform()) {
    const { snapshot } = await FirebaseFirestore.getDocument({ reference: path });
    console.log('Document snapshot.data received', snapshot.data);
    return wrapDoc(snapshot);
  }
  const docRef = webDoc(dbWeb as any, ...refPath(path));
  return webGetDoc(docRef) as unknown as DocSnapshot;
}

export async function setDocument(path: string, data: unknown, merge = false) {
  if (Capacitor.isNativePlatform()) {
    await FirebaseFirestore.setDocument({ reference: path, data: data as any, merge });
  } else {
    const docRef = webDoc(dbWeb as any, ...refPath(path));
    await webSetDoc(docRef, data as any, { merge });
  }
}

export async function updateDocument(path: string, data: unknown) {
  if (Capacitor.isNativePlatform()) {
    await FirebaseFirestore.updateDocument({ reference: path, data: data as any });
  } else {
    const docRef = webDoc(dbWeb as any, ...refPath(path));
    await webUpdateDoc(docRef, data as any);
  }
}

export async function addDocument(path: string, data: unknown) {
  if (Capacitor.isNativePlatform()) {
    const { reference } = await FirebaseFirestore.addDocument({ reference: path, data: data as any });
    return reference.id;
  }
  const colRef = webCollection(dbWeb as any, path);
  const docRef = await webAddDoc(colRef, data as any);
  return docRef.id;
}

export async function getCollection(path: string) {
  console.log('getCollection called for', path);
  if (Capacitor.isNativePlatform()) {
    const res = await FirebaseFirestore.getCollection({ reference: path });
    return wrapCollection(res);
  }
  const colRef2 = webCollection(dbWeb as any, path);
  return webGetDocs(colRef2) as unknown as CollectionSnapshot;
}

// ---------- Functions helper ----------
export async function callFunction<T = unknown, R = unknown>(name: string, data?: T) {
  if (Capacitor.isNativePlatform()) {
    const { data: res } = await FirebaseFunctions.callByName({ name, data });
    return res as R;
  }
  const fn = httpsCallable(functionsWeb, name);
  const res = await fn(data);
  return res.data as R;
}

export {
  auth,
  storage,
  authInitialized,
};
